import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Check, Upload, Wand2, Image as ImageIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DICEBEAR_STYLES = [
  { id: "bigSmile",   label: "Big Smile",   bg: "ffd5dc,fbbf24,a7f3d0" },
  { id: "lorelei",    label: "Lorelei",     bg: "c0aede,d1d4f9,b6e3f4" },
  { id: "avataaars",  label: "Avataaars",   bg: "b6e3f4,c0aede,d1d4f9" },
  { id: "funEmoji",   label: "Fun Emoji",   bg: "ffd5dc,ffdfbf,c0aede" },
  { id: "bottts",     label: "Bots",        bg: "1e293b,334155,475569" },
  { id: "micah",      label: "Micah",       bg: "fde68a,fcd34d,fbbf24" },
  { id: "openPeeps",  label: "Open Peeps",  bg: "a7f3d0,fbcfe8,e9d5ff" },
  { id: "thumbs",     label: "Thumbs",      bg: "fbbf24,f87171,a78bfa" },
];

const AVATAR_STYLES = [
  { id: "cartoon",    label: "Cartoon",    emoji: "🎨", prompt: "Friendly cartoon avatar with warm pastel colours and big expressive eyes. Square portrait." },
  { id: "watercolor", label: "Watercolor", emoji: "🖌️", prompt: "Soft watercolor avatar with gentle brushstrokes on cream paper. Square portrait." },
  { id: "anime",      label: "Anime",      emoji: "🌸", prompt: "Anime-style portrait with soft cel-shading and warm colours. Square avatar." },
  { id: "popart",     label: "Pop Art",    emoji: "✨", prompt: "Bold pop-art avatar with thick outlines, halftone dots, 4-colour palette. Square portrait." },
  { id: "pencil",     label: "Pencil",     emoji: "✏️", prompt: "Detailed pencil-sketch avatar with cross-hatching on white paper. Square." },
  { id: "vintage",    label: "Vintage",    emoji: "📷", prompt: "1970s vintage avatar with sepia tones, film grain, slight vignette. Square." },
  { id: "comic",      label: "Comic",      emoji: "💥", prompt: "Comic-book hero avatar — bold inked outlines, dramatic lighting. Square." },
  { id: "studio",     label: "Studio",     emoji: "💼", prompt: "Professional studio headshot avatar with clean background. Square." },
];

const PROMPT_IDEAS = [
  "A cheerful red panda holding tea, cartoon avatar",
  "A wise koala wearing round glasses, cartoon mascot",
  "A bright sunflower with a smiling face, kids book illustration",
  "A purple cat astronaut among stars, cute mascot",
  "A grandfather kangaroo in a cardigan, gentle cartoon",
  "A magical fairy with butterfly wings, soft pastel illustration",
];

/**
 * Reusable avatar studio.
 * @param endpoints  { put, aiEdit, aiGenerate } — full API paths (relative)
 * @param currentUrl current avatar image URL
 * @param seedName   default seed for dicebear
 * @param onChange   callback (newUrl) => void
 * @param compact    boolean — hide preview header
 */
export default function AvatarStudioCore({ endpoints, currentUrl = "", seedName = "Friend", onChange, compact = false }) {
  const [tab, setTab] = useState("preset");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentUrl);
  const [seed, setSeed] = useState(seedName);
  const [pickedStyle, setPickedStyle] = useState("bigSmile");
  const [uploaded, setUploaded] = useState("");
  const [prompt, setPrompt] = useState(PROMPT_IDEAS[0]);

  const dicebearUrl = (style, name, bg) =>
    `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(name)}&backgroundColor=${bg}&radius=50`;

  const setAvatar = async (val) => {
    setBusy(true);
    try {
      const { data } = await api.put(endpoints.put, { image_b64: val });
      const newUrl = data.avatar_url || data.photo_url || data.url || val;
      setPreviewUrl(newUrl);
      onChange?.(newUrl);
      toast.success("Saved!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setUploaded(reader.result); setPreviewUrl(reader.result); };
    reader.readAsDataURL(file);
  };

  const aiStyle = async (stylePrompt) => {
    if (!uploaded) { toast.error("Upload a photo first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoints.aiEdit, { image_b64: uploaded, prompt: stylePrompt });
      const newUrl = data.avatar_url || data.url;
      setPreviewUrl(newUrl);
      onChange?.(newUrl);
      toast.success("AI avatar created!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoints.aiGenerate, { prompt });
      const newUrl = data.avatar_url || data.url;
      setPreviewUrl(newUrl);
      onChange?.(newUrl);
      toast.success("Conjured!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="avatar-studio-core">
      {!compact && (
        <div className="flex items-start gap-5">
          <div className="relative shrink-0">
            <div className="w-32 h-32 rounded-3xl overflow-hidden ring-4 ring-client/30 bg-muted grid place-items-center" data-testid="avatar-preview">
              {previewUrl ? <img src={previewUrl} alt="avatar" className="w-full h-full object-cover" /> :
                <span className="text-4xl font-display font-black text-client">{seedName?.[0] || "?"}</span>}
              {busy && <div className="absolute inset-0 bg-black/50 grid place-items-center"><div className="w-8 h-8 rounded-full border-4 border-white border-t-transparent animate-spin" /></div>}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-extrabold text-2xl">Avatar</h3>
            <p className="text-sm text-muted-foreground">Pick a fun preset, AI-style your own photo, or conjure one from your imagination.</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full p-1 h-12">
          <TabsTrigger value="preset" className="rounded-full gap-1.5" data-testid="avatar-tab-preset"><ImageIcon className="w-4 h-4" /> Presets</TabsTrigger>
          <TabsTrigger value="upload" className="rounded-full gap-1.5" data-testid="avatar-tab-upload"><Upload className="w-4 h-4" /> Style my photo</TabsTrigger>
          <TabsTrigger value="conjure" className="rounded-full gap-1.5" data-testid="avatar-tab-conjure"><Wand2 className="w-4 h-4" /> Conjure with AI</TabsTrigger>
        </TabsList>

        <TabsContent value="preset" className="space-y-3 mt-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-bold tracking-wider">Avatar seed</Label>
            <Input value={seed} onChange={(e) => setSeed(e.target.value)} className="rounded-xl min-h-[48px]" data-testid="avatar-seed-input" />
            <p className="text-xs text-muted-foreground">Change for unlimited variations of each style.</p>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Pick a style</Label>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {DICEBEAR_STYLES.map((sty) => {
                const url = dicebearUrl(sty.id, seed, sty.bg);
                const active = pickedStyle === sty.id;
                return (
                  <button key={sty.id} type="button" onClick={() => setPickedStyle(sty.id)}
                    data-testid={`preset-style-${sty.id}`}
                    className={`relative rounded-2xl border-2 overflow-hidden card-lift ${active ? "border-client ring-4 ring-client/25" : "border-border hover:border-client/40"}`}>
                    <img src={url} alt={sty.label} className="w-full aspect-square object-cover" />
                    <div className="text-[10px] font-bold py-1 bg-card text-center">{sty.label}</div>
                    {active && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-client text-white grid place-items-center"><Check className="w-3 h-3" strokeWidth={3} /></div>}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={() => setAvatar(dicebearUrl(pickedStyle, seed, DICEBEAR_STYLES.find((s) => s.id === pickedStyle).bg))}
            disabled={busy} data-testid="apply-preset-btn"
            className="rounded-full font-bold gap-2 min-h-[48px]" style={{ background: "hsl(var(--client))" }}>
            <Check className="w-4 h-4" /> Use this avatar
          </Button>
        </TabsContent>

        <TabsContent value="upload" className="space-y-3 mt-4">
          <p className="text-sm text-muted-foreground">Upload a photo, then tap a style to AI-transform it.</p>
          <Input type="file" accept="image/*" onChange={onUpload} className="rounded-xl" data-testid="avatar-upload-input" />
          {uploaded && (
            <div className="space-y-3">
              <Button onClick={() => setAvatar(uploaded)} disabled={busy} variant="outline" className="rounded-full font-bold border-2" data-testid="use-uploaded-btn">
                Use original photo
              </Button>
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Or apply an AI style</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {AVATAR_STYLES.map((st) => (
                    <Button key={st.id} variant="outline" disabled={busy} onClick={() => aiStyle(st.prompt)}
                      data-testid={`avatar-style-${st.id}`}
                      className="rounded-2xl h-auto py-3 flex flex-col gap-1 border-2 hover:border-coral/50 hover:bg-coral/5">
                      <span className="text-2xl">{st.emoji}</span>
                      <span className="font-bold text-xs">{st.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="conjure" className="space-y-3 mt-4">
          <p className="text-sm text-muted-foreground">Describe an avatar — Gemini Nano Banana paints it from scratch.</p>
          <div className="flex flex-wrap gap-1.5">
            {PROMPT_IDEAS.map((p, i) => (
              <button key={i} type="button" onClick={() => setPrompt(p)}
                data-testid={`prompt-idea-${i}`}
                className="rounded-full bg-muted hover:bg-coral/10 text-xs font-semibold px-3 py-1.5 border">{p.slice(0, 40)}…</button>
            ))}
          </div>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className="rounded-xl" data-testid="avatar-prompt-input" />
          <Button onClick={aiGenerate} disabled={busy} className="rounded-full font-bold gap-2 min-h-[48px] text-white"
            style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }} data-testid="conjure-btn">
            <Wand2 className="w-4 h-4" /> {busy ? "Conjuring…" : "Conjure"}
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
